import React, { useCallback, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import type { IntegrationDto, Job } from '../../types';
import CreateJobModal from './CreateJobModal';
import apiClient from '../../shared/api/apiClient';

interface IntegrationSchedulerModalProps {
    integration: IntegrationDto | null;
    isOpen: boolean;
    onClose: () => void;
    onJobDeleted?: () => void;
}

const IntegrationSchedulerModal: React.FC<IntegrationSchedulerModalProps> = ({
    integration,
    isOpen,
    onClose,
    onJobDeleted,
}) => {
    const [jobs, setJobs] = useState<Job[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [showCreateJobModal, setShowCreateJobModal] = useState<boolean>(false);

    // Загрузка задач интеграции
    const loadJobs = useCallback(async () => {
        if (!integration) return;

        try {
            setLoading(true);

            const response = await apiClient.get<Job[]>(`/api/Jobs/${integration.id}`);

            setJobs(response.data);
        } catch (error) {
            console.error('Ошибка при загрузке задач:', error);
            toast.error('Ошибка при загрузке задач: ' + (error instanceof Error ? error.message : 'Неизвестная ошибка'));
        } finally {
            setLoading(false);
        }
    }, [integration]);

    useEffect(() => {
        if (isOpen && integration) {
            loadJobs();
        }
    }, [isOpen, integration, loadJobs]);

    // Handle escape key to close modal
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    const handleOpenCreateJobModal = () => {
        setShowCreateJobModal(true);
    };

    const handleJobCreated = () => {
        loadJobs();
    };

    const handleDeleteJob = async (job: Job) => {
        if (window.confirm(`Вы уверены, что хотите удалить задачу "${job.jobId}"?`)) {
            try {
                await apiClient.delete(`/api/Jobs/${integration?.id}/${job.jobNumber}`);

                setJobs(prev => prev.filter(j => j.jobId !== job.jobId));

                onJobDeleted?.();

                toast.success('Задача удалена успешно!');
            } catch (error) {
                console.error('Ошибка при удалении задачи:', error);
                toast.error('Ошибка при удалении задачи: ' + (error instanceof Error ? error.message : 'Неизвестная ошибка'));
            }
        }
    };

    const formatDate = (dateString: string | null, timeZone?: string | null) => {
        if (!dateString) return 'Не выполнялась';

        const date = new Date(dateString);
        const formattedDate = date.toLocaleString('ru-RU', {
            timeZone: timeZone || undefined,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });

        if (timeZone) {
            return `${formattedDate} (${timeZone})`;
        }

        return formattedDate;
    };

    const getStatusBadge = (job: Job) => {
        if (job.error) {
            return <span className="status-badge error">Ошибка</span>;
        }
        if (job.lastExecution) {
            return <span className="status-badge success">Выполнена</span>;
        }
        return <span className="status-badge pending">Ожидает</span>;
    };

    if (!isOpen || !integration) {
        return null;
    }

    return (
        <div className="modal-backdrop-custom" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="modal-dialog-custom animate-in">
                <div className="modal-content-custom">
                    <div className="modal-header-custom">
                        <div className="d-flex align-items-center">
                            <div className="user-avatar mr-3">
                                ⏰
                            </div>
                            <div>
                                <h3 className="modal-title-custom">Настройки планировщика</h3>
                                <p className="modal-subtitle-custom">Управление задачами интеграции "{integration.name}"</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn-close-custom"
                            onClick={onClose}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>

                    <div className="modal-body-custom">
                        <div className="jobs-section">
                            <div className="jobs-header">
                                <button
                                    className="btn-primary-custom"
                                    onClick={handleOpenCreateJobModal}
                                >
                                    <span className="btn-icon">➕</span>
                                    Создать задачу
                                </button>
                            </div>

                            {loading ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Загрузка...</span>
                                    </div>
                                    <div className="mt-2">Загрузка задач...</div>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover table-striped">
                                        <thead className="table-dark">
                                            <tr>
                                                <th>ID</th>
                                                <th>Cron</th>
                                                <th>Следующее выполнение</th>
                                                <th>Последнее выполнение</th>
                                                <th>Статус</th>
                                                <th>Очередь</th>
                                                <th>Действия</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {jobs.map(job => (
                                                <tr key={job.jobId}>
                                                    <td>{job.jobId.split('-').pop()}</td>
                                                    <td>
                                                        <code>{job.cron}</code>
                                                        {job.timeZoneId && (
                                                            <div className="text-muted small mt-1">
                                                                Часовой пояс: {job.timeZoneId}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>{formatDate(job.nextExecution, job.timeZoneId)}</td>
                                                    <td>{formatDate(job.lastExecution, job.timeZoneId)}</td>
                                                    <td>{getStatusBadge(job)}</td>
                                                    <td>{job.queue}</td>
                                                    <td>
                                                        <div className="btn-group btn-group-sm">
                                                            <button
                                                                className="btn btn-outline-danger btn-sm"
                                                                onClick={() => handleDeleteJob(job)}
                                                                title="Удалить задачу"
                                                            >
                                                                🗑️
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="modal-footer-custom">
                        <button
                            type="button"
                            className="btn-secondary-custom"
                            onClick={onClose}
                        >
                            <span className="btn-icon">✕</span>
                            Закрыть
                        </button>
                    </div>
                </div>
            </div>

            <CreateJobModal
                integration={integration}
                isOpen={showCreateJobModal}
                onClose={() => setShowCreateJobModal(false)}
                onJobCreated={handleJobCreated}
            />
        </div>
    );
};

export default IntegrationSchedulerModal;
